// src/pages/maintenance/ManualTab.js
// Equipment manual upload + AI PM-schedule generation. See docs/MAINTENANCE_MODULE.md
// (Maintenance AI Addendum section) and backend/services/maintenanceAiService.js.
import React, { useState, useEffect, useCallback } from 'react';
import maintenanceService from '../../services/maintenanceService';
import ConfirmModal from '../../components/ConfirmModal';
import AiProgressIndicator from './AiProgressIndicator';

const MANUAL_MAX_BYTES = 32 * 1024 * 1024;

const PM_GENERATION_STAGES = [
  'Reading the manual…',
  'Identifying maintenance tasks…',
  'Extracting intervals and steps…',
  'Checking food-safety priority…',
  'Finalizing the schedule…',
];

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ScheduleCard({ schedule, checked, onToggle }) {
  const [expanded, setExpanded] = useState(false);
  const priorityColor = { critical: 'danger', high: 'warning', medium: 'info', low: 'secondary' }[schedule.priority] || 'secondary';
  return (
    <div className={`card mb-2 ${checked ? '' : 'opacity-50'}`}>
      <div className="card-body py-2">
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-start gap-2">
            <input type="checkbox" className="form-check-input mt-1" checked={checked} onChange={onToggle} />
            <div>
              <span className={`badge bg-${priorityColor}-subtle text-${priorityColor} me-2`} style={{ fontSize: 10 }}>{schedule.priority}</span>
              <strong style={{ fontSize: 14 }}>{schedule.title}</strong>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Every {schedule.intervalDays} day{schedule.intervalDays === 1 ? '' : 's'} · ~{schedule.estimatedHours}h · {schedule.type}
                {schedule.pageReference ? ` · ${schedule.pageReference}` : ''}
              </div>
            </div>
          </div>
          {schedule.steps?.length > 0 && (
            <button className="btn btn-sm btn-link" onClick={() => setExpanded(e => !e)}>{expanded ? 'Hide steps' : `${schedule.steps.length} steps`}</button>
          )}
        </div>
        {schedule.description && <p className="mb-0 mt-1 text-muted" style={{ fontSize: 13, marginLeft: 26 }}>{schedule.description}</p>}
        {expanded && (
          <ol className="mt-2 mb-0" style={{ fontSize: 13, marginLeft: 26 }}>
            {schedule.steps.map((s, i) => (
              <li key={i}>
                {s.instruction}
                {s.requiresPhoto && <span className="badge bg-secondary-subtle text-secondary ms-2" style={{ fontSize: 10 }}>📷 photo</span>}
                {s.requiresValue && <span className="badge bg-secondary-subtle text-secondary ms-2" style={{ fontSize: 10 }}>📏 {s.valueUnit || 'value'}</span>}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

export default function ManualTab({ asset, onAssetUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  const [preview, setPreview] = useState(null); // { schedules, extractionNotes, logId }
  const [selected, setSelected] = useState(new Set()); // indices into preview.schedules
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null); // { created, skipped }
  const [logs, setLogs] = useState([]);

  const loadLogs = useCallback(() => {
    maintenanceService.getPmGenerationLogs(asset.id).then(setLogs).catch(() => setLogs([]));
  }, [asset.id]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { setError('Only PDF manuals are accepted.'); return; }
    if (file.size > MANUAL_MAX_BYTES) { setError('Manual exceeds the 32 MB limit.'); return; }
    setError('');
    setUploading(true);
    try {
      const { uploadUrl, key } = await maintenanceService.getManualUploadUrl(asset.id, file.type, file.size);
      await maintenanceService.uploadToPresignedUrl(uploadUrl, file);
      await maintenanceService.saveManual(asset.id, { s3Key: key, fileName: file.name, sizeBytes: file.size });
      onAssetUpdated();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to upload manual.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    await maintenanceService.deleteManual(asset.id);
    setShowRemoveConfirm(false);
    setPreview(null);
    setResult(null);
    onAssetUpdated();
  };

  const handleDownload = async () => {
    const { url } = await maintenanceService.getManualDownloadUrl(asset.id);
    window.open(url, '_blank');
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGeneratingMessage('');
    setError('');
    setResult(null);
    try {
      const data = await maintenanceService.previewPmGeneration(asset.id, setGeneratingMessage);
      setPreview(data);
      setSelected(new Set(data.schedules.map((_, i) => i))); // select all by default
    } catch (err) {
      setError(err?.message || err?.response?.data?.message || 'Failed to generate PM schedule from manual.');
    } finally {
      setGenerating(false);
    }
  };

  const toggleSelected = (i) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const allSelected = preview && selected.size === preview.schedules.length;
  const toggleSelectAll = () => {
    if (!preview) return;
    setSelected(allSelected ? new Set() : new Set(preview.schedules.map((_, i) => i)));
  };

  const handleConfirmCreate = async () => {
    if (!preview || selected.size === 0) return;
    setConfirming(true);
    setError('');
    try {
      const chosen = preview.schedules.filter((_, i) => selected.has(i));
      const data = await maintenanceService.createPmTemplatesFromSchedules(asset.id, chosen, preview.logId);
      setResult({ created: data.created, skipped: data.skipped });
      setPreview(null);
      loadLogs();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create PM templates.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="row g-3">
      <div className="col-md-5">
        <div className="card">
          <div className="card-body">
            <h6 className="card-title">Equipment Manual</h6>
            {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

            {asset.manualFileName ? (
              <>
                <table className="table table-sm table-borderless mb-2" style={{ fontSize: 13 }}>
                  <tbody>
                    <tr><td className="text-muted">File</td><td>{asset.manualFileName}</td></tr>
                    <tr><td className="text-muted">Size</td><td>{formatBytes(asset.manualSizeBytes)}</td></tr>
                    <tr><td className="text-muted">Uploaded</td><td>{asset.manualUploadedAt ? new Date(asset.manualUploadedAt).toLocaleString() : '—'}</td></tr>
                  </tbody>
                </table>
                <div className="d-flex gap-2 flex-wrap">
                  <button className="btn btn-sm btn-outline-secondary" onClick={handleDownload}>Download</button>
                  <label className="btn btn-sm btn-outline-primary mb-0">
                    {uploading ? 'Uploading…' : 'Replace'}
                    <input type="file" accept="application/pdf" hidden disabled={uploading} onChange={e => handleUpload(e.target.files[0])} />
                  </label>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => setShowRemoveConfirm(true)}>Remove</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-muted" style={{ fontSize: 13 }}>Upload the manufacturer's service manual (PDF, up to 32 MB) to enable AI PM-schedule generation and the Ask AI assistant below.</p>
                <label className="btn btn-sm btn-primary mb-0">
                  {uploading ? 'Uploading…' : 'Upload Manual (PDF)'}
                  <input type="file" accept="application/pdf" hidden disabled={uploading} onChange={e => handleUpload(e.target.files[0])} />
                </label>
              </>
            )}
          </div>
        </div>

        {logs.length > 0 && (
          <div className="card mt-3">
            <div className="card-body">
              <h6 className="card-title">Generation History</h6>
              <ul className="list-unstyled mb-0" style={{ fontSize: 12 }}>
                {logs.map(l => (
                  <li key={l.id} className="mb-1">
                    {new Date(l.createdAt).toLocaleString()} — {l.status === 'success' ? `${l.pmTemplatesCreated} template(s) created` : l.status}
                    {l.TriggeredBy?.username ? ` · ${l.TriggeredBy.username}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className="col-md-7">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="card-title mb-0">AI PM Schedule Generation</h6>
              {asset.manualFileName && !preview && (
                <button className="btn btn-sm btn-primary" disabled={generating} onClick={handleGenerate}>
                  {generating ? 'Generating…' : '✨ Generate PM Schedule from Manual'}
                </button>
              )}
            </div>

            <AiProgressIndicator active={generating} message={generatingMessage} messages={PM_GENERATION_STAGES} />

            {!asset.manualFileName && <p className="text-muted mb-0" style={{ fontSize: 13 }}>Upload a manual first to extract its maintenance schedule.</p>}

            {result && (
              <div className="alert alert-success py-2" style={{ fontSize: 13 }}>
                Created {result.created} PM template{result.created === 1 ? '' : 's'}
                {result.skipped > 0 ? `, skipped ${result.skipped} already-existing` : ''}.
              </div>
            )}

            {preview && (
              <>
                {preview.extractionNotes && (
                  <div className="alert alert-warning py-2" style={{ fontSize: 13 }}>{preview.extractionNotes}</div>
                )}
                {preview.schedules.length === 0 ? (
                  <p className="text-muted mb-0" style={{ fontSize: 13 }}>No maintenance schedule was found in this manual.</p>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center">
                      <p className="text-muted mb-1" style={{ fontSize: 13 }}>
                        Found {preview.schedules.length} recommended task{preview.schedules.length === 1 ? '' : 's'}. Uncheck any you don't need a PM Template for (e.g. a simple wipe-down), then create the rest.
                      </p>
                      <button className="btn btn-sm btn-link" onClick={toggleSelectAll}>{allSelected ? 'Deselect all' : 'Select all'}</button>
                    </div>
                    {preview.schedules.map((s, i) => (
                      <ScheduleCard key={i} schedule={s} checked={selected.has(i)} onToggle={() => toggleSelected(i)} />
                    ))}
                    <div className="d-flex gap-2 mt-2">
                      <button className="btn btn-sm btn-success" disabled={confirming || selected.size === 0} onClick={handleConfirmCreate}>
                        {confirming ? 'Creating…' : selected.size === 0 ? 'Select at least one task' : `Create ${selected.size} PM Template${selected.size === 1 ? '' : 's'}`}
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setPreview(null)}>Cancel</button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        show={showRemoveConfirm}
        title="Remove Manual"
        message="Remove the uploaded manual? AI PM generation and the Ask AI assistant's manual lookup will no longer be available for this asset."
        confirmLabel="Remove"
        onConfirm={handleRemove}
        onCancel={() => setShowRemoveConfirm(false)}
      />
    </div>
  );
}
